import { Link } from "react-router";
import type { FocusData, FocusItem } from "../../api/hub/ai";
import { assignTask } from "../../api/hub/work";
import { updateFocusTask } from "../../api/hub/ai";
import { MutationState, SourceLink, useHubMutation } from "../focus/shared";
import { nextFocusDay } from "../focus/date";
import { DataSource, DataState } from "./Frame";
import { useDataTool, useWorkspaceMe } from "./data";
import { PriorityIcon } from "../../ui/Priority";
const priorityLabels = ["未定优先级", "紧急", "高", "中", "低"];
/** part 让仪表盘把主操作露在外面、其余操作收进详情；默认整组显示。 */
function FocusAction({
  item,
  part = "all",
}: {
  item: FocusItem;
  part?: "all" | "primary" | "secondary";
}) {
  const me = useWorkspaceMe();
  const id = item.task?.id ?? item.taskId ?? item.id;
  const action = useHubMutation(
    async (type: "complete" | "delay" | "delegate") => {
      if (!id) throw new Error("这条重点缺少事项编号，请刷新后重试");
      if (type === "delegate") {
        if (!me.data?.data.id) throw new Error("请先读取当前身份");
        return assignTask(id, me.data.data.id, "agent");
      }
      return updateFocusTask(
        id,
        type === "complete"
          ? { status: "done" }
          : {
              dueDate: nextFocusDay(
                item.task?.dueDate ?? item.dueDate,
                me.data?.data.timezone ?? "Asia/Shanghai",
              ),
            },
      );
    },
  );
  const isTask = item.kind === "task" || !!item.task || !!item.taskId;
  return (
    <>
      <div className="home-actions">
        {isTask ? (
          <>
            {part !== "secondary" && (
              <button
                className="home-primary"
                disabled={action.isPending}
                onClick={() => action.mutate("complete")}
              >
                标记完成
              </button>
            )}
            {part !== "primary" && (
              <>
                <button
                  disabled={action.isPending || !me.data?.data.timezone}
                  onClick={() => action.mutate("delay")}
                >
                  延期一天
                </button>
                <button
                  disabled={action.isPending || !me.data?.data.id}
                  onClick={() => action.mutate("delegate")}
                >
                  交给我的 AI
                </button>
                <Link to={"/tasks?task=" + encodeURIComponent(id ?? "")}>
                  看任务 ↗
                </Link>
              </>
            )}
          </>
        ) : (
          part !== "secondary" && (
            <Link
              className="home-text-action"
              to={
                item.kind === "approval"
                  ? "/approvals?approval=" + encodeURIComponent(id ?? "")
                  : "/inspiration?post=" + encodeURIComponent(id ?? "")
              }
            >
              前往{item.kind === "approval" ? "核对与审批" : "原文阅读"} ↗
            </Link>
          )
        )}
      </div>
      {part !== "primary" && me.isError && (
        <p className="home-error" role="alert">
          时区读取失败，请刷新后再延期。
        </p>
      )}
      <MutationState mutation={action} />
    </>
  );
}
const kindLabel = (item: FocusItem) =>
  item.kind === "approval"
    ? "待你决定"
    : item.kind === "required"
      ? "指定必读"
      : "今天先推进";
/** 仪表盘用的精简版：一行标题和主操作，依据、步骤和其余操作收进「详情」。 */
function dateOnly(value: string, timezone?: string) {
  const date = new Date(value);
  return Number.isFinite(date.getTime())
    ? new Intl.DateTimeFormat("zh-CN", {
        timeZone: timezone ?? "Asia/Shanghai",
        month: "numeric",
        day: "numeric",
      }).format(date)
    : "待核对";
}
function FocusCompact({ item }: { item: FocusItem }) {
  const timezone = useWorkspaceMe().data?.data.timezone;
  const priority = item.task?.priority ?? item.priority ?? 0,
    due = item.task?.dueDate ?? item.dueDate;
  return (
    <article className="home-focus-compact">
      <div className="home-focus-line">
        <span className="home-pri" data-urgent={priority === 1}>
          <PriorityIcon priority={priority} label={false} />
          {priorityLabels[priority]}
        </span>
        <div className="home-focus-title">
          <h3>{item.title}</h3>
          {(item.kind === "approval" || item.kind === "required" || due) && (
            <p>
              {[
                item.kind === "approval" || item.kind === "required"
                  ? kindLabel(item)
                  : "",
                due ? "截止 " + dateOnly(due, timezone) : "",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
        <FocusAction item={item} part="primary" />
      </div>
      <details className="home-focus-details">
        <summary>依据与更多操作</summary>
        <p className="home-reason">
          <strong>为什么是它：</strong>
          {item.reason ??
            item.reasons?.join("；") ??
            "尚未返回排序依据，请核对原记录。"}
        </p>
        {!!item.steps?.length && (
          <ol className="home-focus-steps">
            {item.steps.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>
        )}
        <SourceLink href={item.sourceUrl ?? item.task?.sourceUrl}>
          核对原始依据 ↗
        </SourceLink>
        {item.planSource === "local" && item.planNote && (
          <p className="home-muted">{item.planNote}</p>
        )}
        <FocusAction item={item} part="secondary" />
      </details>
    </article>
  );
}
export function FocusToday({
  max = 5,
  compact = false,
}: {
  max?: number;
  compact?: boolean;
}) {
  const query = useDataTool<FocusData>("focus_today");
  const items = query.data?.data.items?.slice(0, max) ?? [];
  return (
    <DataState
      query={query}
      isEmpty={!items.length}
      empty={
        <>
          今天没有待推进的重点。<Link to="/tasks">查看任务</Link>
          ，或到项目核对下一步。
        </>
      }
    >
      {compact && (
        <div className="home-focus-items is-compact">
          {items.map((item, index) => (
            <FocusCompact key={item.id ?? index} item={item} />
          ))}
        </div>
      )}
      {!compact && (
        <div className="home-focus-items">
          {items.map((item, index) => (
            <article
              key={item.id ?? index}
              className={index === 0 ? "home-focus-lead" : "home-focus-row"}
            >
              <div className="home-row-meta">
                <span className="home-pri" data-urgent={(item.task?.priority ?? item.priority) === 1}>
                  <PriorityIcon priority={item.task?.priority ?? item.priority ?? 0} label={false} />
                  {priorityLabels[item.task?.priority ?? item.priority ?? 0]}
                </span>
                {(item.task?.dueDate ?? item.dueDate) && (
                  <span>截止 {item.task?.dueDate ?? item.dueDate}</span>
                )}
                <span>{kindLabel(item)}</span>
              </div>
              <h3>{item.title}</h3>
              <p className="home-reason">
                <strong>为什么是它：</strong>
                {item.reason ??
                  item.reasons?.join("；") ??
                  "尚未返回排序依据，请核对原记录。"}
              </p>
              {item.steps?.length ? (
                <details open={index === 0} className="home-steps">
                  <summary>接下来怎么做</summary>
                  <ol>
                    {item.steps.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </details>
              ) : (
                <p className="home-muted">尚未返回行动步骤，请先核对任务。</p>
              )}
              {item.planSource === "local" && item.planNote && (
                <p className="home-muted">{item.planNote}</p>
              )}
              <SourceLink href={item.sourceUrl ?? item.task?.sourceUrl}>
                核对原始依据 ↗
              </SourceLink>
              <FocusAction item={item} />
            </article>
          ))}
        </div>
      )}
      <DataSource result={query.data} href="/focus" />
    </DataState>
  );
}
